<template>
  <div class="stall-order-detail">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="back-btn" @click="goBack">← {{ $t('common.back') }} / Back</div>
      <h1>{{ $t('order.orderDetail') }} / Order Detail</h1>
      <div class="order-no">{{ order.orderNo }}</div>
    </div>

    <div v-loading="loading" class="detail-content">
      <!-- 状态卡片 -->
      <div class="status-bar" :class="statusBarClass">
        <div class="s-icon">{{ statusIcon }}</div>
        <div class="s-info">
          <div class="s-title">{{ statusTitle }}</div>
          <div class="s-sub">{{ order.createdAt }} · {{ order.sourceChannel }}</div>
        </div>
      </div>

      <!-- 时间线 -->
      <div class="timeline-wrap">
        <h3>{{ $t('order.progress') }}</h3>
        <div class="timeline">
          <div class="track" :style="{ width: trackWidth }"></div>
          <div class="tl-step" v-for="(s, i) in timeline" :key="i">
            <div class="tl-dot" :class="dotClass(i)">{{ s.icon }}</div>
            <div class="tl-label" :class="{ active: i === currentStep }">{{ s.label }}</div>
            <div class="tl-time">{{ s.time || '-' }}</div>
          </div>
        </div>
      </div>

      <!-- 模块1: 商品 -->
      <div class="module">
        <div class="module-header">
          <span class="icon">📦</span><span class="title">{{ $t('navigation.products') }} / Items</span>
          <span class="badge blue">{{ order.items ? order.items.length : 0 }}{{ $t("order.items") }}</span>
        </div>
        <div class="module-body">
          <div class="prod-item" v-for="item in order.items" :key="item.id">
            <div class="prod-info">
              <div class="prod-name">{{ item.productName }}</div>
              <div class="prod-spec">{{ item.specification || '' }}</div>
              <span class="prod-tax" :class="item.taxRate && item.taxRate > 0 ? 'gst' : 'free'">
                {{ item.taxRate && item.taxRate > 0 ? 'GST ' + Math.round(item.taxRate * 100) + '%' : $t('order.taxFree') }}
              </span>
            </div>
            <div class="prod-right">
              <div class="prod-amount">S$ {{ formatAmount(item.subtotal) }}</div>
              <div class="prod-qty">×{{ item.quantity }}</div>
            </div>
          </div>
          <div style="margin-top: 12px;">
            <div class="amt-row"><span class="label">{{ $t('navigation.products') }}{{ $t('cart.subtotal') }}</span><span class="value">S$ {{ formatAmount(order.subtotal) }}</span></div>
            <div class="amt-row"><span class="label">GST {{ $t('payment.tax') }}</span><span class="value">S$ {{ formatAmount(order.gstAmount) }}</span></div>
            <div class="amt-row total"><span class="label">{{ $t("order.totalPayable") }}</span><span class="value">S$ {{ formatAmount(order.totalAmount) }}</span></div>
            <!-- 已付金额（按状态分组累加） -->
            <div v-if="Object.keys(paidByStatus).length > 0" class="amt-row" style="border-top:2px solid #f1f5f9;margin-top:8px;padding-top:12px">
              <span class="label" style="font-size:15px;font-weight:700;color:#1e293b">{{ $t('payment.paid') }}{{ $t('common.amount') }}</span>
              <span class="value" style="display:flex;gap:16px;flex-wrap:wrap">
                <span v-for="(g, k) in paidByStatus" :key="k" :style="{ color: g.color, fontSize:'16px', fontWeight:'700' }">{{ g.label }} S$ {{ g.amount }}</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- 模块2: 批发商 -->
      <div class="module">
        <div class="module-header">
          <span class="icon">🏭</span><span class="title">{{ $t("order.wholesaler") }}</span>
        </div>
        <div class="module-body">
          <div style="display:flex;align-items:center;gap:12px;padding:4px 0;">
            <div class="ws-avatar">🏭</div>
            <div style="flex:1">
              <div class="ws-name">{{ order.wholesalerName || $t('order.wholesaler') }}</div>
              <div class="ws-desc">{{ $t('stall.wholesale') }}</div>
            </div>
            <el-button size="mini" type="success" icon="el-icon-chat-dot-round" @click="contactWhatsapp" :disabled="!wholesalerWhatsapp">
              💬 {{ wholesalerWhatsapp ? $t('order.contactWhatsapp') : $t('order.contactWhatsappDisabled') }}
            </el-button>
          </div>
          <div style="margin-top:8px">
            <div class="row"><span class="label">{{ $t('order.orderType') }}</span><span class="value">{{ orderTypeText }}</span></div>
            <div class="row"><span class="label">{{ $t("order.invoiceNo") }}</span><span class="value mono">{{ order.invoiceNo || '-' }}</span></div>
            <div class="row"><span class="label">{{ $t("order.invoiceUuid") }}</span><span class="value mono small">{{ order.invoiceUuid || '-' }}</span></div>
          </div>
        </div>
      </div>

      <!-- 模块3: 物流 -->
      <div class="module">
        <div class="module-header">
          <span class="icon">🚚</span><span class="title">{{ $t("delivery.title") }}</span>
        </div>
        <div class="module-body">
          <div class="row"><span class="label">{{ $t('delivery.deliveryMethod') }}</span><span class="value">{{ deliveryMethodText }}</span></div>
          <div class="row"><span class="label">{{ $t('order.deliveryAddress') }}</span><span class="value">{{ order.deliveryAddress || '-' }}</span></div>
          <div class="row"><span class="label">{{ $t('user.contact') }}</span><span class="value">{{ order.customerName }} · {{ order.customerPhone || '-' }}</span></div>
        </div>
      </div>

      <!-- 模块4: 支付与售后 -->
      <div class="module">
        <div class="module-header">
          <span class="icon">💳</span><span class="title">{{ $t("payment.title") }} / {{ $t("afterSales.title") }}</span>
        </div>
        <div class="module-body">
          <!-- 我要付款按钮（未支付状态下显示） -->
          <div v-if="canInitiatePayment" class="pay-action-bar">
            <button class="btn-pay-now" @click="showPayDialog = true">
              💳 {{ $t("order.payNow") }}
            </button>
            <div class="pay-tip">{{ $t("order.payTip") }}</div>
          </div>

          <!-- 支付弹窗 -->
          <div v-if="showPayDialog" class="pay-dialog-overlay" @click.self="showPayDialog = false">
            <div class="pay-dialog">
              <div class="pay-dialog-header">
                <h3>💳 {{ $t("order.orderDetail") }} - {{ $t("payment.title") }}</h3>
                <button class="close-btn" @click="showPayDialog = false">×</button>
              </div>
              <div class="pay-dialog-body">
                <!-- 付款金额 -->
                <div class="pay-amount">
                  <div class="pay-amount-label">{{ $t('payment.paymentAmount') }}</div>
                  <div class="pay-amount-value">S$ {{ formatAmount(order.totalAmount) }}</div>
                </div>

                <!-- 支付方式 -->
                <div class="pay-method-section">
                  <div class="pay-method-label">{{ $t('payment.paymentMethod') }}</div>
                  <div class="pay-method-options">
                    <div class="pay-method-option" :class="{ active: payForm.method === 'CASH' }" @click="payForm.method = 'CASH'">
                      <span class="pay-method-icon">💵</span>
                      <span class="pay-method-text">{{ $t("order.cash") }}</span>
                    </div>
                    <div class="pay-method-option" :class="{ active: payForm.method === 'PAYNOW' }" @click="payForm.method = 'PAYNOW'">
                      <span class="pay-method-icon">📱</span>
                      <span class="pay-method-text">PayNow</span>
                    </div>
                    <div class="pay-method-option" :class="{ active: payForm.method === 'BANK_TRANSFER' }" @click="payForm.method = 'BANK_TRANSFER'">
                      <span class="pay-method-icon">🏦</span>
                      <span class="pay-method-text">{{ $t("order.transfer") }}</span>
                    </div>
                  </div>
                </div>

                <!-- PayNow 收款码 -->
                <div v-if="payForm.method === 'PAYNOW'" class="pay-qr-section">
                  <div v-if="loadingPayNowQr" class="pay-qr-loading">
                    <span class="loading-spinner">⏳</span>
                    <p>{{ $t("order.loadingQr") }}</p>
                  </div>
                  <div v-else-if="paynowQrUrl" class="pay-qr-display">
                    <p class="pay-qr-tip">{{ $t("order.scanTip") }}</p>
                    <img :src="getImageUrl(paynowQrUrl)" alt="PayNow QR" class="pay-qr-img" />
                  </div>
                  <div v-else class="pay-qr-empty">
                    <p>⚠️ {{ $t("order.noQr") }}</p>
                  </div>
                </div>

                <!-- 银行转账信息 -->
                <div v-if="payForm.method === 'BANK_TRANSFER'" class="pay-bank-section">
                  <div v-if="loadingBankInfo" class="pay-qr-loading">
                    <span class="loading-spinner">⏳</span>
                    <p>{{ $t("order.loadingBank") }}</p>
                  </div>
                  <div v-else-if="bankInfo.bankName" class="pay-bank-info">
                    <div class="bank-row"><span class="bank-label">{{ $t("order.bankName") }}：</span><span class="bank-value">{{ bankInfo.bankName }}</span></div>
                    <div class="bank-row"><span class="bank-label">{{ $t("order.accountName") }}：</span><span class="bank-value">{{ bankInfo.bankAccountName }}</span></div>
                    <div class="bank-row"><span class="bank-label">{{ $t("order.accountNumber") }}：</span><span class="bank-value highlight">{{ bankInfo.bankAccountNumber }}</span></div>
                  </div>
                  <div v-else class="pay-qr-empty">
                    <p>⚠️ {{ $t("order.noBank") }}</p>
                  </div>
                </div>

                <!-- 支付凭证上传 -->
                <div v-if="payForm.method === 'PAYNOW' || payForm.method === 'BANK_TRANSFER'" class="pay-proof-section">
                  <div class="pay-proof-label">{{ $t('payment.uploadProof') }}</div>
                  <div class="pay-proof-area" @click="triggerProofUpload">
                    <img v-if="payForm.proofUrl" :src="getImageUrl(payForm.proofUrl)" :alt="$t('Stall.StallOrderDetail.line184_alt')" class="pay-proof-img" />
                    <div v-else class="pay-proof-placeholder">
                      <span class="proof-icon">📤</span>
                      <p>{{ $t("order.uploadProof") }}</p>
                    </div>
                  </div>
                  <input ref="proofInput" type="file" accept="image/*" style="display:none" @change="handlePayProofUpload" />
                </div>
              </div>
              <div class="pay-dialog-footer">
                <button class="btn-cancel" @click="showPayDialog = false">{{ $t('common.cancel') }}</button>
                <button class="btn-confirm" @click="submitPayment" :disabled="paySubmitting">{{ paySubmitting ? $t('order.submitting') : $t('order.confirmPay') }}</button>
              </div>
            </div>
          </div>

          <!-- 支付流水列表（只读显示，退款走售后） -->
          <div v-if="paymentRecords.length > 0" style="margin-bottom:16px">
            <div style="font-size:13px;font-weight:600;color:#64748b;margin-bottom:8px">{{ $t("order.paymentFlow") }}</div>
            <div v-for="r in paymentRecords" :key="r.id" style="background:#f8fafc;padding:10px 12px;border-radius:8px;margin-bottom:8px;border-left:3px solid #6366f1">
              <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:4px">
                <span style="font-size:12px;font-weight:600;color:#4b5563;font-family:monospace">{{ r.paymentNo }}</span>
                <span class="record-status" :class="getRecordStatusClass(r.status)">{{ getRecordStatusText(r.status) }}</span>
              </div>
              <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px">
                <span style="font-size:14px;font-weight:700;color:#1e293b">S$ {{ formatAmount(r.amount) }}</span>
                <span style="font-size:12px;color:#6b7280">{{ r.paymentMethod }}</span>
              </div>
              <div style="font-size:11px;color:#9ca3af;margin-bottom:4px">
                {{ r.createdAt || '-' }}
                <span v-if="r.confirmedAt" style="color:#10b981;margin-left:8px">{{ $t("order.confirmedAt") }}: {{ r.confirmedAt }}</span>
              </div>
              <!-- 付款凭证 -->
              <div v-if="r.transactionRef" style="margin-bottom:6px">
                <img :src="getImageUrl(r.transactionRef)" :alt="$t('order.paymentVoucher')" class="proof-image" @click="previewProof(r.transactionRef)" />
              </div>
              <!-- 上传凭证（仅待确认状态） -->
              <div v-if="r.status === 'pending_confirm'" style="margin-top:8px">
                <el-upload
                  :action="uploadUrl"
                  :headers="uploadHeaders"
                  :show-file-list="false"
                  :on-success="handleProofUploadSuccess"
                  accept="image/*"
                >
                  <el-button size="mini" type="primary">📎 {{ $t('payment.uploadProof') }}</el-button>
                </el-upload>
              </div>
            </div>
          </div>

          <!-- 售后记录 -->
          <div style="font-size:13px;font-weight:600;color:#64748b;margin-bottom:8px">{{ $t("order.afterSalesRecords") }}</div>
          <!-- 有售后记录时显示 -->
          <div v-if="afterSales.length > 0">
            <div
              v-for="as in afterSales"
              :key="as.id"
              class="as-card"
              @click="$router.push(`/stall/after-sales/${as.id}`)"
            >
              <div class="as-header">
                <span class="as-no">{{ as.afterSalesNo }}</span>
                <span class="as-status" :class="asStatusClass(as.status)">{{ asStatusText(as.status) }}</span>
              </div>
              <div class="as-body">
                <div class="as-left">
                  <div class="as-reason">{{ asReasonIcon(as.reasonCode) }} {{ asReasonText(as.reasonCode) }}</div>
                  <div class="as-time">{{ as.requestedAt }} · {{ asResolutionText(as.resolutionType) }}</div>
                </div>
                <div class="as-right">
                  <div class="as-amount">S$ {{ centsToAmount(as.refundAmount) }}</div>
                  <div class="as-sub">{{ asResolutionStatusText(as.resolutionStatus) }}</div>
                </div>
              </div>
            </div>
            <!-- 打印退单小票 -->
            <div style="margin-top:8px;">
              <el-button size="small" style="width:100%" @click="printReceipt">
                🖨️ {{ $t("order.printRefundReceipt") }}
              </el-button>
            </div>
          </div>

          <!-- 无售后记录时显示 -->
          <div v-else>
            <div class="empty-state">
              <div class="empty-icon">✅</div>
              <div class="empty-text">{{ $t("order.noAfterSales") }}</div>
            </div>
          </div>

          <!-- 申请售后按钮 -->
          <div v-if="canShowAfterSales" style="margin-top:8px">
            <el-button type="primary" style="width:100%" @click="handleApplyAfterSales">
              📝 {{ $t("afterSales.title") }}
            </el-button>
          </div>
        </div>
      </div>



      <div style="height:80px"></div>
    </div>

    <!-- 底部操作栏 -->
    <div class="action-bar">
      <el-button type="success" size="small" @click="contactWhatsapp" :disabled="!wholesalerWhatsapp">💬 {{ $t('order.contactMerchant') }}</el-button>
      <el-button v-if="canCancelOrder" type="danger" size="small" @click="handleCancel">❌ {{ $t('common.cancel') }}</el-button>
      <el-button v-if="canShowAfterSales" type="warning" size="small" @click="handleApplyAfterSales">{{ $t("order.afterSales") }}</el-button>
      <el-button type="primary" size="small" @click="handleReorder">{{ $t('order.reorder') }}</el-button>
    </div>

    <!-- 申请售后弹窗 -->
    <el-dialog
      :title="'📝 ' + $t('afterSales.title')"
      :visible.sync="showApplyDialog"
      width="90%"
      class="apply-dialog"
    >
      <div v-if="order && order.id" class="apply-form">
        <div class="apply-info">📋 {{ $t("order.orderNo") }}: {{ order.orderNo }} · S$ {{ formatAmount(order.totalAmount) }}</div>

        <!-- 问题原因 -->
        <div class="form-section">
          <div class="form-label">{{ $t("afterSales.returnReason") }}</div>
          <div class="reason-tags">
            <span
              v-for="r in reasons"
              :key="r.value"
              class="reason-tag"
              :class="{ active: applyForm.reasonCode === r.value }"
              @click="applyForm.reasonCode = r.value"
            >
              {{ r.icon }} {{ r.label }}
            </span>
          </div>
        </div>

        <!-- 问题描述 -->
        <div class="form-section">
          <div class="form-label">{{ $t('afterSales.problemDesc') }}</div>
          <el-input
            v-model="applyForm.reasonText"
            type="textarea"
            :rows="3"
            :placeholder="$t('afterSales.problemDescPlaceholder')"
            maxlength="500"
            show-word-limit
          ></el-input>
        </div>

        <!-- 上传凭证 -->
        <div class="form-section">
          <div class="form-label">📸 {{ $t('payment.uploadProof') }}</div>
          <el-upload
            :action="uploadUrl"
            :headers="uploadHeaders"
            list-type="picture-card"
            :file-list="applyForm.evidenceFiles"
            :on-success="handleUploadSuccess"
            :on-remove="handleUploadRemove"
            :limit="9"
            accept="image/*"
          >
            <i class="el-icon-plus"></i>
          </el-upload>
        </div>

        <!-- 涉及商品 -->
        <div class="form-section">
          <div class="form-label">{{ $t('order.returnItems') }}</div>
          <div
            v-for="item in order.items"
            :key="item.id"
            class="product-select"
          >
            <el-checkbox v-model="item._selected"></el-checkbox>
            <div class="prod-info">
              <div class="prod-name">{{ item.productName }}</div>
              <div class="prod-spec">×{{ item.quantity }} · S$ {{ formatAmount(item.unitPrice) }}</div>
            </div>
            <div class="prod-qty-ctrl">
              <el-button size="mini" icon="el-icon-minus" circle @click="decreaseQty(item)"></el-button>
              <span class="qty-num">{{ item._qty || 0 }}</span>
              <el-button size="mini" icon="el-icon-plus" circle @click="increaseQty(item)"></el-button>
            </div>
          </div>
        </div>

        <!-- 预估退款 -->
        <div class="refund-preview">
          <div class="refund-label">💰 {{ $t("order.estimatedRefund") }}</div>
          <div class="refund-row">
            <span>{{ $t("navigation.products") }}{{ $t("common.amount") }}: S$ {{ estimateRefund.item }}</span>
            <span>GST: S$ {{ estimateRefund.gst }}</span>
          </div>
          <div class="refund-total">{{ $t("common.total") }}: S$ {{ estimateRefund.total }}</div>
        </div>

        <!-- 提交 -->
        <div class="form-actions">
          <el-button @click="showApplyDialog = false">{{ $t('common.cancel') }}</el-button>
          <el-button type="primary" @click="submitApply">{{ $t('afterSales.submitApplication') }}</el-button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getOrderDetail, cancelOrder } from '@/services/order';
import { applyAfterSales, getAfterSalesList, getRefundReceipt } from '@/services/afterSales';
import { getImageUrl } from '@/utils/image';

export default {
  name: 'StallOrderDetail',
  data() {
    return {
      order: {},
      loading: false,
      afterSales: [],
      paymentRecords: [],
      // 申请售后
      showApplyDialog: false,
      applyForm: {
        reasonCode: '',
        reasonText: '',
        evidenceFiles: [],
        evidenceUrls: []
      },
      reasons: [
        { value: 'DAMAGED', icon: '📦', label: '' },
        { value: 'WRONG_ITEM', icon: '❌', label: '' },
        { value: 'NOT_RECEIVED', icon: '❓', label: '' },
        { value: 'WRONG_SPEC', icon: '📏', label: '' },
        { value: 'SHORT_DELIVERY', icon: '📉', label: '' },
        { value: 'DONT_WANT', icon: '🙅', label: '' },
        { value: 'OTHER', icon: '📝', label: '' }
      ],
      uploadUrl: process.env.VUE_APP_API_URL + '/files/upload-image',
      uploadHeaders: {
        Authorization: 'Bearer ' + (localStorage.getItem('auth_token') || '')
      },
      // 付款弹窗
      showPayDialog: false,
      payForm: {
        method: 'PAYNOW',
        proofUrl: ''
      },
      paySubmitting: false,
      paynowQrUrl: '',
      loadingPayNowQr: false,
      bankInfo: { bankName: '', bankAccountName: '', bankAccountNumber: '' },
      loadingBankInfo: false,
      wholesalerWhatsapp: ''
    };
  },
  computed: {
    timeline() {
      const statuses = [
        { label: this.$t('order.confirmed'), field: 'createdAt' },
        { label: '', field: null },
        { label: this.$t('delivery.inTransit'), field: null },
        { label: this.$t('delivery.delivered'), field: null }
      ];
      const s = (this.order.status || '').toLowerCase();
      let currentIdx = 0;
      if (s === 'delivered') currentIdx = 3;
      else if (s === 'dispatching') currentIdx = 2;
      else if (s === 'ready_to_ship') currentIdx = 1;
      else if (s === 'confirmed') currentIdx = 0;
      else if (s === 'pending') currentIdx = 0;
      else if (s === 'cancelled') currentIdx = -1;

      return statuses.map((step, i) => ({
        icon: currentIdx === -1 ? '❌' : (i <= currentIdx ? '✓' : String(i + 1)),
        label: currentIdx === -1 ? this.$t('orderStatus.cancelled') : step.label,
        time: step.field ? this.order[step.field] : (i === currentIdx ? this.order.createdAt : null)
      }));
    },
    currentStep() {
      const s = (this.order.status || '').toLowerCase();
      if (s === 'delivered') return 3;
      if (s === 'dispatching') return 2;
      if (s === 'ready_to_ship') return 1;
      if (s === 'confirmed') return 0;
      if (s === 'pending') return 0;
      return -1;
    },
    trackWidth() {
      if (this.currentStep < 0) return '0%';
      return `${((this.currentStep + 1) / 4) * 100}%`;
    },
    statusIcon() {
      const m = { pending: '⏳', confirmed: '📋', ready_to_ship: '📦', dispatching: '🚚', delivered: '🎉', cancelled: '❌' };
      return m[this.order.status] || '📦';
    },
    statusTitle() {
      const m = {
        pending: '',
        confirmed: '',
        ready_to_ship: '',
        dispatching: '',
        delivered: '',
        cancelled: ''
      };
      return m[this.order.status] || '';
    },
    statusBarClass() {
      if (this.order.status === 'cancelled') return 'cancelled';
      if (['delivered', 'dispatching', 'ready_to_ship', 'confirmed', 'pending'].includes(this.order.status)) return 'processing';
      return 'pending';
    },
    orderTypeText() {
      const m = { SELF: '自助下单', AGENT: '代客下单', DELIVERY: '配送员下单' };
      return m[this.order.orderType] || this.order.orderType;
    },
    deliveryMethodText() {
      return this.order.deliveryMethod === 'SELF_PICKUP' ? '自提' : this.order.deliveryMethod || '-';
    },
    /** 按状态分组的已付金额（仅确认/成功） */
    paidByStatus() {
      if (!this.paymentRecords || this.paymentRecords.length === 0) return {};
      const groups = {
        confirmed: { label: this.$t('order.confirmed'), amount: 0, color: '#10b981' },
        success: { label: '', amount: 0, color: '#10b981' },
      };
      for (const r of this.paymentRecords) {
        const g = groups[r.status];
        if (g) g.amount += Number(r.amount || 0);
      }
      const result = {};
      for (const [k, v] of Object.entries(groups)) {
        if (v.amount > 0) result[k] = { ...v, amount: v.amount.toFixed(2) };
      }
      return result;
    },
    canCancel() {
      return ['processing', 'packed', 'confirmed'].includes(this.order.status);
    },
    canInitiatePayment() {
      const s = (this.order.status || '').toLowerCase();
      const unpaidStatuses = ['pending', 'confirmed', 'packing', 'ready_to_ship', 'processing', 'packed'];
      const hasPaymentRecord = this.paymentRecords.some(r => {
        const rs = (r.status || '').toLowerCase();
        return ['pending_confirm', 'confirmed', 'success', 'pending'].includes(rs);
      });
      return unpaidStatuses.includes(s) && !hasPaymentRecord && (this.order.totalAmount > 0);
    },
    hasPendingAfterSales() {
      return this.afterSales.some(as =>
        ['PENDING_REVIEW', 'REVIEWED'].includes(as.status)
      );
    },
    canCancelOrder() {
      return this.order.canCancel === true;
    },
    canShowAfterSales() {
      return this.order.canAfterSales === true;
    },
    handleApplyAfterSales() {
      this.showApplyDialog = true;
    },
    estimateRefund() {
      let itemTotal = 0;
      let gstTotal = 0;
      if (this.order.items) {
        this.order.items.forEach(item => {
          if (item._selected && item._qty > 0) {
            const sub = item.unitPrice * item._qty;
            itemTotal += sub;
            gstTotal += sub * (item.taxRate || 0);
          }
        });
      }
      return {
        item: itemTotal.toFixed(2),
        gst: gstTotal.toFixed(2),
        total: (itemTotal + gstTotal).toFixed(2)
      };
    }
  },
  watch: {
    showPayDialog(val) {
      if (val) {
        this.payForm = { method: 'PAYNOW', proofUrl: '' };
        this.loadPayNowQr();
        this.loadBankInfo();
      }
    }
  },
  created() {
    this.loadOrderDetail();
  },
  methods: {
    getImageUrl,
    async loadOrderDetail() {
      this.loading = true;
      try {
        const id = this.$route.params.id;
        this.order = await getOrderDetail(id);
        console.log('Order detail:', this.order);
        // 订单加载完成后再加载支付记录
        if (this.order && this.order.id) {
          this.loadPaymentRecords();
          this.loadAfterSales();
          this.loadWholesalerInfo();
        }
      } catch (e) {
        this.$message.error(this.$t('order.loadFailed') + ': ' + (e.response?.data?.message || e.message));
      } finally {
        this.loading = false;
      }
    },
    async loadPaymentRecords() {
      try {
        const api = (await import('@/services/api')).default;
        const res = await api.get(`/orders/${this.order.id}/payment-records`);
        // API 拦截器已返回 response.data
        this.paymentRecords = Array.isArray(res) ? res : (res.data || []);
        console.log('Payment records:', this.paymentRecords.length);
      } catch (e) {
        console.error('Failed to load payment records', e);
        this.paymentRecords = [];
      }
    },
    async handleProofUploadSuccess(response) {
      if (response && response.url) {
        this.$message.success(this.$t('order.uploadSuccess'));
        await this.loadPaymentRecords();
      }
    },
    previewProof(url) {
      window.open(url, '_blank');
    },
    async initiatePayment() {
      // 此方法已废弃，改用弹窗方式
    },
    // 加载 PayNow 收款码
    async loadPayNowQr() {
      try {
        this.loadingPayNowQr = true;
        const wholesalerId = this.order.wholesalerId;
        if (!wholesalerId) return;
        const api = (await import('@/services/api')).default;
        const response = await api.get('/shop/info', { params: { wholesalerId } });
        if (response && response.paynowQrUrl) {
          this.paynowQrUrl = response.paynowQrUrl;
        }
      } catch (e) {
        console.error('Failed to load PayNow QR:', e);
      } finally {
        this.loadingPayNowQr = false;
      }
    },
    // 加载银行信息
    async loadBankInfo() {
      try {
        this.loadingBankInfo = true;
        const wholesalerId = this.order.wholesalerId;
        if (!wholesalerId) return;
        const api = (await import('@/services/api')).default;
        const response = await api.get('/shop/info', { params: { wholesalerId } });
        if (response) {
          this.bankInfo = {
            bankName: response.bankName || '',
            bankAccountName: response.bankAccountName || '',
            bankAccountNumber: response.bankAccountNumber || ''
          };
        }
      } catch (e) {
        console.error('Failed to load bank info:', e);
      } finally {
        this.loadingBankInfo = false;
      }
    },
    // 加载批发商信息（WhatsApp号码）
    async loadWholesalerInfo() {
      try {
        const wholesalerId = this.order.wholesalerId;
        if (!wholesalerId) return;
        const api = (await import('@/services/api')).default;
        const response = await api.get('/shop/info', { params: { wholesalerId } });
        if (response) {
          this.wholesalerWhatsapp = response.whatsappNumber || '';
        }
      } catch (e) {
        console.error('Failed to load wholesaler info:', e);
      }
    },
    // WhatsApp联系商家
    contactWhatsapp() {
      if (!this.wholesalerWhatsapp) {
        this.$message.warning(this.$t('order.contactWhatsappNoConfig'));
        return;
      }
      // 构造消息内容
      const orderNo = this.order.orderNo || '';
      const message = `你好，关于订单 ${orderNo}，我想咨询一下...`;
      // 打开WhatsApp
      const url = `https://wa.me/${this.wholesalerWhatsapp}?text=${encodeURIComponent(message)}`;
      window.open(url, '_blank');
    },
    triggerProofUpload() {
      this.$refs.proofInput && this.$refs.proofInput.click();
    },
    async handlePayProofUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      try {
        const formData = new FormData();
        formData.append('file', file);
        const api = (await import('@/services/api')).default;
        const res = await api.post('/files/upload-image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (res && res.url) {
          this.payForm.proofUrl = res.url;
        }
      } catch (e) {
        this.$message.error(this.$t('order.uploadFailed'));
      } finally {
        event.target.value = '';
      }
    },
    async submitPayment() {
      // 验证
      if (this.payForm.method === 'PAYNOW' && !this.payForm.proofUrl && !this.paynowQrUrl) {
        // PayNow 没上传凭证且有收款码时提示
        if (this.paynowQrUrl) {
          this.$message.warning(this.$t('payment.uploadProof'));
          return;
        }
      }
      if (this.payForm.method === 'BANK_TRANSFER' && !this.payForm.proofUrl && this.bankInfo.bankName) {
        this.$message.warning(this.$t('payment.uploadProof'));
        return;
      }

      this.paySubmitting = true;
      try {
        const api = (await import('@/services/api')).default;
        
        // 组装请求数据
        const payData = {
          paymentMethod: this.payForm.method === 'PAYNOW' ? 'PayNow' : (this.payForm.method === 'BANK_TRANSFER' ? this.$t('order.transfer') : this.$t('order.cash'))
        };
        if (this.payForm.proofUrl) {
          payData.transactionRef = this.payForm.proofUrl;
        }
        
        // 发起支付（创建支付流水，带凭证）
        const res = await api.post(`/orders/${this.order.id}/initiate-payment`, payData);
        
        // 关闭弹窗
        this.showPayDialog = false;
        this.payForm = { method: 'PAYNOW', proofUrl: '' };
        
        this.$message.success(this.$t('order.paymentSubmitted'));
        this.loadOrderDetail();
      } catch (e) {
        this.showPayDialog = false;
        if (e !== 'cancel') {
          this.$message.error(this.$t('order.submitFailed') + ': ' + (e.response?.data?.message || e.message));
        }
      } finally {
        this.paySubmitting = false;
      }
    },
    async confirmPayment() {
      try {
        await this.$confirm(this.$t('order.confirmPayMsg'), this.$t('order.confirmPayTitle'), {
          confirmButtonText: this.$t('common.confirm'),
          cancelButtonText: this.$t('common.cancel'),
          type: 'warning',
          dangerouslyUseHTMLString: true
        });
        // 调用后端确认支付接口
        const api = (await import('@/services/api')).default;
        await api.post(`/orders/${this.order.id}/confirm-payment`);
        this.$message.success(this.$t('order.confirmSubmitted'));
        this.loadOrderDetail();
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error(this.$t('order.confirmPayFailed') + ': ' + (e.response?.data?.message || e.message));
        }
      }
    },
    async handleCancel() {
      try {
        await this.$confirm(this.$t('order.cancelConfirm'), this.$t('order.cancelTitle'), {
          confirmButtonText: this.$t('order.confirmCancel'),
          cancelButtonText: this.$t('order.thinkAgain'),
          type: 'warning'
        });
        await cancelOrder(this.order.id, { reason: '客户取消' });
        this.$message.success(this.$t('order.cancelSuccess'));
        this.loadOrderDetail();
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error(this.$t('order.cancelFailed') + ': ' + (e.response?.data?.message || e.message));
        }
      }
    },
    dotClass(i) {
      if (i < this.currentStep) return 'done';
      if (i === this.currentStep) return 'current';
      return '';
    },
    formatAmount(amount) {
      return Number(amount || 0).toFixed(2);
    },
    getRecordStatusClass(status) {
      const m = { pending_confirm: 'pending', confirmed: 'success', success: 'success', failed: 'failed' };
      return m[status] || 'pending';
    },
    getRecordStatusText(status) {
      const m = { pending_confirm: '待确认', confirmed: '支付确认', success: '支付成功', failed: '已拒绝' };
      return m[status] || status;
    },
    goBack() {
      this.$router.back();
    },
    goToAfterSalesList() {
      this.$router.push(`/stall/after-sales?orderId=${this.order.id}`);
    },
    handleReorder() {
      // 将订单商品复制到购物车（字段名需匹配 StallCart 期望的 name/price）
      const cartItems = (this.order.items || []).map(item => ({
        id: item.productId || item.id,
        productId: item.productId,
        name: item.productName,
        price: item.unitPrice,
        quantity: item.quantity,
        taxRate: item.taxRate || 0,
        specification: item.specification || '',
        wholesalerId: this.order.wholesalerId,
        wholesalerName: this.order.wholesalerName || '',
        // 保留原订单信息
        originalOrderId: this.order.id,
        originalItemId: item.id
      }));
      
      // 保存到 localStorage
      try {
        localStorage.setItem('stall_cart', JSON.stringify(cartItems));
        this.$message.success(this.$t('order.reorder') + ' ✓');
        this.$router.push('/stall/cart');
      } catch (e) {
        console.error('保存购物车失败:', e);
        this.$message.error('保存失败，请重试');
      }
    },
    // ===== 售后相关方法 =====
    async loadAfterSales() {
      try {
        const res = await getAfterSalesList({ orderId: this.order.id, page: 0, size: 20 });
        this.afterSales = res.content || [];
      } catch (e) {
        console.error('Failed to load after-sales list', e);
        this.afterSales = [];
      }
    },
    asStatusClass(status) {
      const map = {
        PENDING_REVIEW: 'status-pending',
        REVIEWED: 'status-reviewed',
        COMPLETED: 'status-completed',
        REJECTED: 'status-rejected',
        CANCELLED: 'status-cancelled'
      };
      return map[status] || '';
    },
    asStatusText(status) {
      const map = {
        PENDING_REVIEW: '⏳ 待审核',
        REVIEWED: '🔄 处理中',
        COMPLETED: '✅ 已完成',
        REJECTED: '❌ 已拒绝',
        CANCELLED: '🚫 已取消'
      };
      return map[status] || status;
    },
    asReasonIcon(code) {
      const map = { DAMAGED: '📦', WRONG_ITEM: '❌', NOT_RECEIVED: '❓', WRONG_SPEC: '📏', SHORT_DELIVERY: '📉', DONT_WANT: '🙅', OTHER: '📝' };
      return map[code] || '📋';
    },
    asReasonText(code) {
      const map = { DAMAGED: '商品破损', WRONG_ITEM: '发错货了', NOT_RECEIVED: '没收到货', WRONG_SPEC: '规格不对', SHORT_DELIVERY: '少发了', DONT_WANT: '不想要了', OTHER: '其他' };
      return map[code] || code;
    },
    asResolutionText(type) {
      if (!type) return this.$t('order.waitingReview');
      const map = { RETURN_REFUND: '退货退款', EXCHANGE: '换货', RESEND: '补发', PARTIAL_REFUND: '部分退款', RELIEF: '减免', NO_ACTION: '不处理' };
      return '方案: ' + (map[type] || type);
    },
    asResolutionStatusText(status) {
      if (!status) return '';
      const map = { PICKING_UP: '取货中', RETURN_RECEIVED: '已收到退货', INSPECTING: '质检中', REFUND_PROCESSING: '退款处理中', EXCHANGE_SHIPPING: '换货发货中', EXCHANGE_DELIVERED: '换货已送达', RESEND_SHIPPING: '补发中', RESEND_DELIVERED: '补发已送达' };
      return map[status] || status;
    },
    centsToAmount(cents) {
      if (!cents) return '0.00';
      return (cents / 100).toFixed(2);
    },
    increaseQty(item) {
      if (!item._qty) item._qty = 0;
      if (item._qty < item.quantity) item._qty++;
    },
    decreaseQty(item) {
      if (item._qty && item._qty > 0) item._qty--;
    },
    handleUploadSuccess(response) {
      if (response && response.url) {
        this.applyForm.evidenceUrls.push(response.url);
      }
    },
    handleUploadRemove(file) {
      const idx = this.applyForm.evidenceUrls.indexOf(file.response?.url);
      if (idx > -1) this.applyForm.evidenceUrls.splice(idx, 1);
    },
    async submitApply() {
      // 验证
      if (!this.applyForm.reasonCode) {
        this.$message.warning('请选择问题原因');
        return;
      }
      if (!this.applyForm.reasonText || !this.applyForm.reasonText.trim()) {
        this.$message.warning('请填写问题描述');
        return;
      }
      // 获取选中商品
      const applyItems = (this.order.items || [])
        .filter(item => item._selected && item._qty > 0)
        .map(item => ({
          orderItemId: item.id,
          productId: item.productId,
          productName: item.productName,
          quantity: item._qty,
          unitPrice: item.unitPrice,
          subtotal: item.unitPrice * item._qty
        }));
      if (applyItems.length === 0) {
        this.$message.warning('请至少选择一个商品');
        return;
      }
      try {
        await applyAfterSales({
          orderId: this.order.id,
          reasonCode: this.applyForm.reasonCode,
          reasonText: this.applyForm.reasonText,
          evidenceUrls: JSON.stringify(this.applyForm.evidenceUrls),
          applyItems: JSON.stringify(applyItems)
        });
        this.$message.success('售后申请已提交');
        this.showApplyDialog = false;
        this.applyForm = { reasonCode: '', reasonText: '', evidenceFiles: [], evidenceUrls: [] };
        this.loadAfterSales();
      } catch (e) {
        this.$message.error('申请失败: ' + (e.message || e));
      }
    },
    async printReceipt() {
      try {
        if (this.afterSales.length > 0) {
          const res = await getRefundReceipt(this.afterSales[0].id);
          const receipt = res.receipt;
          if (receipt) {
            this.$message.success('退单小票已生成，请发送到打印机');
            console.log('退单小票内容:', receipt);
            // TODO: 调用打印服务
          }
        } else {
          this.$message.warning('暂无可打印的退单小票');
        }
      } catch (e) {
        this.$message.error('生成小票失败: ' + (e.message || e));
      }
    }
  }
};
</script>

<style scoped>
.stall-order-detail { min-height: 100vh; background: #f0f2f5; }

.page-header {
  padding: calc(16px + env(safe-area-inset-top, 0px)); background: #fff; border-bottom: 1px solid #e2e8f0;
}
.page-header .back-btn { font-size: 13px; color: #64748b; cursor: pointer; }
.page-header h1 { font-size: 18px; font-weight: 700; margin-top: 4px; }
.page-header .order-no {
  font-size: 12px; color: #94a3b8; margin-top: 4px;
  font-family: 'SF Mono', 'Menlo', monospace;
}

.detail-content { padding-bottom: 20px; }

/* 状态卡片 */
.status-bar {
  margin: 12px 12px 0; border-radius: 14px; padding: 20px 16px;
  display: flex; align-items: center; gap: 14px; color: #fff;
  position: relative; overflow: hidden;
}
.status-bar::after {
  content: ''; position: absolute; top: -50%; right: -20%;
  width: 200px; height: 200px; border-radius: 50%;
  background: rgba(255,255,255,0.06);
}
.status-bar.paid { background: linear-gradient(135deg, #10b981, #059669); }
.status-bar.unpaid { background: linear-gradient(135deg, #f59e0b, #d97706); }
.status-bar.processing { background: linear-gradient(135deg, #6366f1, #8b5cf6); }
.status-bar .s-icon { font-size: 38px; z-index: 1; }
.status-bar .s-info { z-index: 1; }
.status-bar .s-title { font-size: 18px; font-weight: 700; }
.status-bar .s-sub { font-size: 12px; opacity: 0.8; margin-top: 2px; }

/* 时间线 */
.timeline-wrap {
  margin: 12px 12px 0; background: #fff; border-radius: 14px;
  padding: 16px; box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}
.timeline-wrap h3 { font-size: 14px; font-weight: 700; margin-bottom: 16px; }
.timeline { display: flex; position: relative; padding: 0 10px; }
.timeline::before {
  content: ''; position: absolute; top: 16px; left: 24px; right: 24px;
  height: 3px; background: #e2e8f0; border-radius: 2px; z-index: 0;
}
.timeline .track {
  position: absolute; top: 16px; left: 24px;
  height: 3px; background: linear-gradient(90deg, #10b981, #6366f1);
  border-radius: 2px; z-index: 0; transition: width 0.4s ease;
}
.tl-step {
  flex: 1; display: flex; flex-direction: column; align-items: center;
  position: relative; z-index: 1;
}
.tl-dot {
  width: 32px; height: 32px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 14px; font-weight: 700; margin-bottom: 8px;
  background: #e2e8f0; color: #94a3b8; transition: all 0.3s;
}
.tl-dot.done { background: #10b981; color: #fff; }
.tl-dot.current { background: #6366f1; color: #fff; animation: pulse 2s infinite; }
.tl-label { font-size: 11px; color: #94a3b8; text-align: center; line-height: 1.2; }
.tl-label.active { color: #6366f1; font-weight: 600; }
.tl-time { font-size: 10px; color: #cbd5e1; margin-top: 3px; }

@keyframes pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(99,102,241,0.3); }
  50% { box-shadow: 0 0 0 8px rgba(99,102,241,0); }
}

/* 模块通用 */
.module {
  margin: 12px 12px 0; background: #fff; border-radius: 14px;
  overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}
.module-header {
  padding: 14px 16px; display: flex; align-items: center; gap: 8px;
  border-bottom: 1px solid #f1f5f9;
}
.module-header .icon { font-size: 20px; }
.module-header .title { font-size: 15px; font-weight: 700; color: #1e293b; flex: 1; }
.badge {
  font-size: 11px; padding: 2px 10px; border-radius: 20px; font-weight: 600;
}
.badge.blue { background: #dbeafe; color: #1e40af; }
.badge.green { background: #dcfce7; color: #166534; }
.badge.red { background: #fee2e2; color: #991b1b; }
.nav-arrow { color: #94a3b8; font-size: 14px; }
.module-body { padding: 12px 16px; }

.row {
  display: flex; justify-content: space-between; align-items: flex-start;
  padding: 8px 0; font-size: 13px;
}
.row + .row { border-top: 1px solid #f8fafc; }
.row .label { color: #94a3b8; flex-shrink: 0; min-width: 70px; }
.row .value { color: #334155; font-weight: 500; text-align: right; word-break: break-all; }
.mono { font-family: 'SF Mono', 'Menlo', monospace; font-size: 12px; }
.mono.small { font-size: 10px; }

/* 商品 */
.prod-item {
  display: flex; gap: 10px; padding: 10px 0; border-bottom: 1px solid #f8fafc;
}
.prod-item:last-child { border-bottom: none; }
.prod-info { flex: 1; min-width: 0; }
.prod-name { font-size: 13px; font-weight: 600; color: #1e293b; }
.prod-spec { font-size: 11px; color: #94a3b8; margin-top: 2px; }
.prod-tax {
  font-size: 10px; padding: 1px 6px; border-radius: 4px; display: inline-block; margin-top: 2px;
}
.prod-tax.gst { background: #fef3c7; color: #92400e; }
.prod-tax.free { background: #dcfce7; color: #166534; }
.prod-right { text-align: right; flex-shrink: 0; }
.prod-amount { font-size: 14px; font-weight: 700; color: #1e293b; }
.prod-qty { font-size: 11px; color: #94a3b8; margin-top: 2px; }

.amt-row {
  display: flex; justify-content: space-between; padding: 6px 0; font-size: 13px;
}
.amt-row .label { color: #94a3b8; }
.amt-row .value { color: #334155; }
.amt-row.total {
  border-top: 2px solid #f1f5f9; margin-top: 8px; padding-top: 12px;
}
.amt-row.total .label { font-size: 15px; font-weight: 700; color: #1e293b; }
.amt-row.total .value { font-size: 20px; font-weight: 800; color: #6366f1; }

/* 批发商 */
.ws-avatar {
  width: 40px; height: 40px; border-radius: 10px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  display: flex; align-items: center; justify-content: center;
  font-size: 18px; flex-shrink: 0;
}
.ws-name { font-size: 14px; font-weight: 700; color: #1e293b; }
.ws-desc { font-size: 11px; color: #94a3b8; margin-top: 2px; }

/* 支付 */
.pay-status { text-align: center; padding: 8px 0 4px; }
.pay-badge {
  display: inline-block; padding: 6px 24px; border-radius: 24px;
  font-size: 15px; font-weight: 700;
}
.pay-badge.green { background: #dcfce7; color: #166534; }
.pay-badge.red { background: #fee2e2; color: #991b1b; }
.total-amt { font-size: 18px !important; font-weight: 800 !important; color: #6366f1 !important; }
.hint { font-size: 11px; color: #94a3b8; text-align: center; margin-top: 6px; }

/* 我要付款操作栏 */
.pay-action-bar {
  text-align: center;
  padding: 16px 0;
  margin-bottom: 16px;
  background: linear-gradient(135deg, #f0f4ff, #f8f0ff);
  border-radius: 12px;
}
.btn-pay-now {
  padding: 14px 48px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(102,126,234,0.4);
  transition: all 0.2s;
}
.btn-pay-now:active {
  transform: scale(0.98);
  box-shadow: 0 2px 6px rgba(102,126,234,0.3);
}
.pay-tip {
  font-size: 12px;
  color: #999;
  margin-top: 8px;
}

/* 售后 */
.empty-state { text-align: center; padding: 24px 0; color: #94a3b8; }
.empty-state .empty-icon { font-size: 32px; margin-bottom: 8px; }
.empty-state .empty-text { font-size: 13px; }

/* 售后卡片 */
.as-card {
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 10px;
  margin-bottom: 8px;
  cursor: pointer;
}
.as-card:last-child { margin-bottom: 0; }
.as-card:hover { border-color: #52b788; }
.as-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
.as-no { font-size: 11px; color: #999; font-family: monospace; }
.as-status { font-size: 10px; padding: 2px 6px; border-radius: 8px; }
.as-status.status-pending { background: #fff7e6; color: #fa8c16; }
.as-status.status-reviewed { background: #e6f7ff; color: #1890ff; }
.as-status.status-completed { background: #f6ffed; color: #52c41a; }
.as-status.status-rejected { background: #fff2f0; color: #ff4d4f; }
.as-status.status-cancelled { background: #fafafa; color: #999; }
.as-body { display: flex; justify-content: space-between; align-items: center; }
.as-left { flex: 1; }
.as-reason { font-size: 12px; margin-bottom: 2px; }
.as-time { font-size: 10px; color: #bbb; }
.as-right { text-align: right; margin-left: 8px; }
.as-amount { font-size: 14px; color: #ff4d4f; font-weight: 600; }
.as-sub { font-size: 10px; color: #1890ff; margin-top: 2px; }

/* 付款凭证 */
.proof-image {
  max-width: 100%; max-height: 200px; border-radius: 8px;
  cursor: pointer; border: 2px solid #e2e8f0;
  transition: transform 0.2s;
}
.proof-image:hover { transform: scale(1.02); border-color: #6366f1; }

/* 流水记录状态 */
.record-status { font-size: 11px; padding: 2px 8px; border-radius: 10px; font-weight: 600; }
.record-status.pending { background: #fef3c7; color: #92400e; }
.record-status.success { background: #dcfce7; color: #166534; }
.record-status.failed { background: #fee2e2; color: #991b1b; }

/* 申请售后弹窗 */
.apply-info {
  background: #f0f7ff;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  color: #457b9d;
  margin-bottom: 16px;
}
.form-section { margin-bottom: 16px; }
.form-label { font-size: 13px; font-weight: 600; margin-bottom: 8px; }
.reason-tags { display: flex; flex-wrap: wrap; gap: 6px; }
.reason-tag {
  padding: 6px 10px;
  border: 1px solid #e8e8e8;
  border-radius: 16px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}
.reason-tag.active { border-color: #1890ff; background: #e6f7ff; color: #1890ff; }

.product-select {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  margin-bottom: 6px;
}
.product-select .prod-info { flex: 1; }
.product-select .prod-name { font-size: 13px; font-weight: 500; }
.product-select .prod-spec { font-size: 11px; color: #999; }
.product-select .prod-qty-ctrl { display: flex; align-items: center; gap: 4px; }
.product-select .qty-num { min-width: 24px; text-align: center; font-size: 13px; }

.refund-preview {
  background: #fff7e6;
  padding: 10px 12px;
  border-radius: 6px;
  margin-bottom: 16px;
}
.refund-label { font-size: 12px; color: #fa8c16; margin-bottom: 4px; }
.refund-row { display: flex; justify-content: space-between; font-size: 12px; }
.refund-total { font-size: 15px; font-weight: 700; color: #ff4d4f; margin-top: 4px; }
.form-actions { display: flex; gap: 8px; }

/* 流水记录 */
.record-list { display: flex; flex-direction: column; gap: 10px; }
.record-item {
  padding: 12px; background: #f8fafc; border-radius: 8px;
  border-left: 3px solid #6366f1;
}
.record-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
.record-no { font-size: 12px; font-weight: 600; color: #4b5563; font-family: monospace; }
.record-status { font-size: 11px; padding: 2px 8px; border-radius: 10px; font-weight: 600; }
.record-status.pending { background: #fef3c7; color: #92400e; }
.record-status.success { background: #dcfce7; color: #166534; }
.record-status.failed { background: #fee2e2; color: #991b1b; }
.record-body { display: flex; justify-content: space-between; margin-bottom: 4px; }
.record-amount { font-size: 15px; font-weight: 700; color: #1e293b; }
.record-method { font-size: 12px; color: #6b7280; }
.record-footer { display: flex; justify-content: space-between; font-size: 11px; color: #9ca3af; }
.record-confirmed { color: #10b981; }

/* 底部操作栏 */
.action-bar {
  position: fixed; bottom: 0; left: 50%; transform: translateX(-50%);
  width: 100%; max-width: 480px; background: #fff;
  padding: 10px 16px; display: flex; gap: 8px;
  border-top: 1px solid #e2e8f0; z-index: 100;
  box-shadow: 0 -2px 12px rgba(0,0,0,0.06);
}

/* 付款弹窗 */
.pay-dialog-overlay {
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5); z-index: 9999;
  display: flex; align-items: center; justify-content: center;
}
.pay-dialog {
  background: #fff; border-radius: 16px;
  width: 92%; max-width: 420px; max-height: 90vh; overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0,0,0,0.3);
}
.pay-dialog-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 16px 20px; border-bottom: 1px solid #f0f0f0;
}
.pay-dialog-header h3 { margin: 0; font-size: 18px; color: #333; }
.close-btn { background: none; border: none; font-size: 28px; color: #999; cursor: pointer; }
.pay-dialog-body { padding: 20px; }

/* 付款金额 */
.pay-amount {
  text-align: center; padding: 16px; background: linear-gradient(135deg, #f0f4ff, #f8f0ff);
  border-radius: 12px; margin-bottom: 16px;
}
.pay-amount-label { font-size: 13px; color: #999; margin-bottom: 4px; }
.pay-amount-value { font-size: 28px; font-weight: 800; color: #6366f1; }

/* 支付方式 */
.pay-method-section { margin-bottom: 16px; }
.pay-method-label { font-size: 13px; color: #666; margin-bottom: 8px; font-weight: 600; }
.pay-method-options { display: flex; gap: 10px; }
.pay-method-option {
  flex: 1; text-align: center; padding: 12px 8px;
  border: 2px solid #e8e8e8; border-radius: 10px;
  cursor: pointer; transition: all 0.2s;
}
.pay-method-option.active {
  border-color: #6366f1; background: #f0f0ff;
}
.pay-method-icon { font-size: 24px; display: block; margin-bottom: 4px; }
.pay-method-text { font-size: 12px; color: #333; font-weight: 500; }

/* 收款码 */
.pay-qr-section { margin-bottom: 16px; }
.pay-qr-loading { text-align: center; padding: 16px; color: #999; }
.loading-spinner { font-size: 24px; }
.pay-qr-display { text-align: center; }
.pay-qr-tip { font-size: 13px; color: #666; margin-bottom: 12px; }
.pay-qr-img { max-width: 240px; width: 100%; border-radius: 8px; }
.pay-qr-empty { text-align: center; padding: 16px; color: #f59e0b; font-size: 13px; }

/* 银行信息 */
.pay-bank-section { margin-bottom: 16px; }
.pay-bank-info { background: #f8f9fa; border-radius: 8px; padding: 12px; }
.bank-row { display: flex; justify-content: space-between; padding: 6px 0; font-size: 13px; }
.bank-label { color: #666; }
.bank-value { font-weight: 600; color: #333; }
.bank-value.highlight { color: #6366f1; font-size: 15px; }

/* 凭证上传 */
.pay-proof-section { margin-bottom: 0; }
.pay-proof-label { font-size: 13px; color: #666; margin-bottom: 8px; font-weight: 600; }
.pay-proof-area {
  background: #f8f9fa; border: 2px dashed #d1d5db; border-radius: 10px;
  padding: 20px; text-align: center; cursor: pointer; transition: all 0.2s;
}
.pay-proof-area:hover { border-color: #6366f1; }
.pay-proof-img { max-width: 240px; width: 100%; border-radius: 8px; }
.pay-proof-placeholder { padding: 20px; color: #999; }
.proof-icon { font-size: 32px; }

/* 弹窗底部 */
.pay-dialog-footer {
  display: flex; gap: 10px; padding: 16px 20px;
  border-top: 1px solid #f0f0f0;
}
.btn-cancel {
  flex: 1; padding: 12px; border: 1px solid #e0e0e0;
  border-radius: 10px; background: #fff; font-size: 14px;
  cursor: pointer; color: #666;
}
.btn-confirm {
  flex: 2; padding: 12px; border: none;
  border-radius: 10px; background: linear-gradient(135deg, #667eea, #764ba2);
  color: #fff; font-size: 15px; font-weight: 600; cursor: pointer;
}
.btn-confirm:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
